import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationInterceptor } from '../interceptor/notification.interceptor';
import { OfflineEntitlementService } from './offline-entitlement.service';
import { OfflineSaveService } from './offline-save.service';
import { OfflineMediaService } from './offline-media.service';
import { UseraccessserviceService } from '../useraccessservice.service';
import { getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';
import { ToastrService } from 'ngx-toastr';

export type ConnectivityState = 'online' | 'degraded' | 'offline';

// Shared across every same-origin frame of this app, which is the point — see
// readSharedState below. localStorage is already per-origin, and each school is its own
// subdomain, so this is tenant-scoped for free; no key namespacing needed and none added.
const SHARED_STATE_KEY = 'impata.offline.connectivity';

// How long a state written by another frame is worth trusting. Long enough that the iframe
// booting inside a note inherits what its parent learned seconds ago, short enough that a
// stale 'offline' from a previous session can't make a genuinely-connected app start in the
// wrong state. Any successful API request calls reportSuccess() and corrects it immediately
// regardless.
const SHARED_STATE_TTL_MS = 60000;

// A saved lesson's note embeds its video as a same-app <iframe src="/media?..."> (Quill's
// MediaBlot), and that iframe boots a SECOND, completely separate instance of this app with
// its own copy of this service. Seeded from navigator.onLine alone, that instance believed
// it was online in the single most common way a connection dies — Wi-Fi still associated,
// no route to the internet, navigator.onLine still true — so it skipped the downloaded copy
// and went to the network for the stream, which then hung. This service's own header already
// says navigator.onLine lies in exactly that case; the second instance had no other source
// of truth to consult. Now it has one: whatever the outer app most recently worked out.
//
// navigator.onLine is still consulted FIRST when it says false, because false is the one
// answer it never gets wrong.
function readSharedState(): ConnectivityState | null {
  try {
    const raw = localStorage.getItem(SHARED_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: ConnectivityState; at?: number };
    if (!parsed?.state || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > SHARED_STATE_TTL_MS) return null;
    return parsed.state;
  } catch {
    return null; // private mode, quota, corrupt value — never let this break startup
  }
}

function initialState(): ConnectivityState {
  if (navigator.onLine === false) return 'offline';
  return readSharedState() ?? 'online';
}

// Replaces navigator.onLine-only detection (D7: online-first with timeout, not a binary
// switch — navigator.onLine lies on captive portals and dying connections, the common case).
// Fed passively by JwtInterceptorService (every app-API request's success/status-0 already
// tells us what we need, for free) and actively by a heartbeat that only runs while degraded
// and the tab is visible, plus one probe on the browser's own 'online' event.
@Injectable({
  providedIn: 'root',
})
export class OfflineConnectivityService {
  private readonly stateSubject = new BehaviorSubject<ConnectivityState>(initialState());
  readonly state$ = this.stateSubject.asObservable();

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private consecutiveFailures = 0;

  constructor(private http: HttpClient, private notificationInterceptor: NotificationInterceptor, private injector: Injector) {
    window.addEventListener('online', () => this.handleBrowserOnlineEvent());
    window.addEventListener('offline', () => this.setState('offline'));
    document.addEventListener('visibilitychange', () => this.maybeStartHeartbeat());

    // Keeps already-open frames in step, not just ones booting now: the note's /media iframe
    // can be sitting there when the outer app is the one that discovers the connection died.
    // 'storage' fires in every same-origin document EXCEPT the one that wrote, so this never
    // hears its own write. It adopts the value WITHOUT re-persisting it — writing back is
    // what would make two frames echo each other indefinitely.
    window.addEventListener('storage', (event) => {
      if (event.key !== SHARED_STATE_KEY) return;
      const shared = readSharedState();
      if (shared && shared !== this.stateSubject.value) this.stateSubject.next(shared);
    });

    // ->online triggers an entitlement refresh + stale-content revalidation (plan §5.1/§6.6).
    // Resolved lazily via Injector, not constructor-injected — OfflineSaveService pulls in
    // ClassmanagementService/ModulemanagementService/etc., and this keeps that whole graph
    // out of this service's own constructor chain until a reconnect actually happens.
    let wasOnline = this.currentState === 'online';
    this.state$.subscribe((state) => {
      const isOnlineNow = state === 'online';
      if (isOnlineNow && !wasOnline) {
        this.onReconnected();
      }
      wasOnline = isOnlineNow;
    });
  }

  private async onReconnected(): Promise<void> {
    const entitlement = this.injector.get(OfflineEntitlementService);
    // Same rule as app.component: only sweep on server truth we actually received.
    const refreshed = await entitlement.refresh();
    if (refreshed) await entitlement.sweepIfExpired(); // unenrollment/refund/expulsion since the last refresh
    await entitlement.refreshEnrolledModuleSummaries();
    this.injector.get(OfflineSaveService).revalidateVersions();
    this.notifyIfSavedVideosSuperseded();
  }

  // revalidateVersions only walks IndexRecords, which cover lessons and notes; saved videos
  // live in the `media` store alone and have never been revalidated anywhere. Notes refresh
  // themselves silently, but a video is large enough that re-downloading is the student's
  // call - so this only tells them, and the downloads hub is where they act on it.
  private async notifyIfSavedVideosSuperseded(): Promise<void> {
    try {
      const school = getCurrentSchoolSubdomain();
      const user = getCurrentUserId(this.injector.get(UseraccessserviceService).getCurrentUserToken());
      if (!school || !user) return;

      const stale = await this.injector.get(OfflineMediaService).findStaleSavedVideos(school, user);
      if (!stale.length) return;

      this.injector
        .get(ToastrService)
        .info(
          `${stale.length} saved video${stale.length === 1 ? ' has' : 's have'} been updated. Open Downloads to save the new version.`,
          'Offline downloads',
          { timeOut: 8000 },
        );
    } catch (err) {
      console.error('[offline] saved-video staleness check failed', err);
    }
  }

  get currentState(): ConnectivityState {
    return this.stateSubject.value;
  }

  // Called by JwtInterceptorService on a genuine network failure (status 0) for any request
  // to this app's own API. Free signal — no extra request, this piggybacks on real traffic.
  reportFailure(): void {
    this.consecutiveFailures++;
    if (!navigator.onLine || this.consecutiveFailures >= 2) {
      this.setState(navigator.onLine ? 'degraded' : 'offline');
    } else if (this.currentState === 'online') {
      this.setState('degraded');
    }
    this.maybeStartHeartbeat();
  }

  // Called by JwtInterceptorService on any successful response to this app's own API.
  reportSuccess(): void {
    this.consecutiveFailures = 0;
    this.setState('online');
    this.stopHeartbeat();
  }

  private handleBrowserOnlineEvent(): void {
    this.consecutiveFailures = 0;
    this.probeOnce();
  }

  private maybeStartHeartbeat(): void {
    if (this.heartbeatTimer || this.currentState === 'online') return;
    if (document.visibilityState !== 'visible') return;
    this.heartbeatTimer = setInterval(() => this.probeOnce(), 10000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // Reuses the existing useraccount/isalive endpoint — no new endpoint for this. Suspends
  // NotificationInterceptor's toast for the duration so a heartbeat failure doesn't spam
  // "Unable to connect to server" every 10 seconds while genuinely offline.
  private probeOnce(): void {
    const wasSuspended = this.notificationInterceptor.SuspendNotification;
    this.notificationInterceptor.SuspendNotification = true;
    this.http.get(environment.ImpataAppAPIRoot + 'useraccount/isalive').subscribe({
      next: () => {
        this.notificationInterceptor.SuspendNotification = wasSuspended;
        this.reportSuccess();
      },
      error: () => {
        this.notificationInterceptor.SuspendNotification = wasSuspended;
        this.reportFailure();
      },
    });
  }

  private setState(next: ConnectivityState): void {
    // Persisted on EVERY call, including one that doesn't change the state, because the
    // timestamp is what readSharedState checks against SHARED_STATE_TTL_MS. A student sitting
    // offline is re-confirmed as offline by the heartbeat every 10s but the state never
    // changes, so a write-on-change-only would let the shared value go stale and the next
    // frame to boot would fall back to navigator.onLine — the exact lie this exists to avoid.
    // No feedback loop: a frame that hears this via 'storage' updates its own subject
    // directly (see the listener in the constructor) and never comes back through here.
    this.persistSharedState(next);
    if (this.stateSubject.value === next) return;
    this.stateSubject.next(next);
  }

  private persistSharedState(state: ConnectivityState): void {
    try {
      localStorage.setItem(SHARED_STATE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      /* private mode or quota — the app must still work, just without cross-frame sharing */
    }
  }
}
